import {getExpertByUid, getExpertByHandle} from '../../../../firebase/firestore/experts';
import {getFintelsByAuthorUid} from '../../../../firebase/firestore/reports';

/**
 * Module-level caches keyed by author UID / handle.
 *
 * The drafts/FINTEL tables render one AuthorChip per row, and the same author
 * frequently appears across multiple rows. Without caching, every chip that
 * resolves its author would fire its own `/api/experts/...` and
 * `/api/reports?status=approved&authorUid=:uid` request. These caches collapse
 * all of those into a single in-flight request per key, shared across rows and
 * even across simultaneously-open dialogs.
 *
 * Promises (not resolved values) are cached so concurrent callers for the same
 * key dedupe onto one network round-trip. A rejected lookup is evicted so a
 * later attempt can retry.
 */
const expertByUidCache = new Map();
const expertByHandleCache = new Map();
const fintelCache = new Map();

/**
 * Resolve the expert profile for a UID, deduplicated and cached.
 * @param {string} uid
 * @returns {Promise<object|null>}
 */
export const getCachedExpertByUid = (uid) => {
    if (!uid) return Promise.resolve(null);
    if (!expertByUidCache.has(uid)) {
        const promise = getExpertByUid(uid).catch((err) => {
            expertByUidCache.delete(uid);
            throw err;
        });
        expertByUidCache.set(uid, promise);
    }
    return expertByUidCache.get(uid);
};

/**
 * Resolve the expert profile for a handle, deduplicated and cached.
 * @param {string} handle
 * @returns {Promise<object|null>}
 */
export const getCachedExpertByHandle = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!expertByHandleCache.has(handle)) {
        const promise = getExpertByHandle(handle).catch((err) => {
            expertByHandleCache.delete(handle);
            throw err;
        });
        expertByHandleCache.set(handle, promise);
    }
    return expertByHandleCache.get(handle);
};

/**
 * Resolve an expert profile, preferring a known handle (mirrors the original
 * AuthorChip behavior which queried experts/{handle} when available) and
 * falling back to the by-UID endpoint.
 * @param {string} uid
 * @param {string} [handle]
 * @returns {Promise<object|null>}
 */
export const getCachedExpert = (uid, handle) => {
    if (handle) return getCachedExpertByHandle(handle);
    return getCachedExpertByUid(uid);
};

/**
 * Resolve the approved FINTELs authored by a UID, deduplicated and cached.
 * @param {string} uid
 * @returns {Promise<Array<object>>}
 */
export const getCachedFintels = (uid) => {
    if (!uid) return Promise.resolve([]);
    if (!fintelCache.has(uid)) {
        const promise = getFintelsByAuthorUid(uid).catch((err) => {
            fintelCache.delete(uid);
            throw err;
        });
        fintelCache.set(uid, promise);
    }
    return fintelCache.get(uid);
};

/**
 * Clear cached author data. Optionally scope to a single UID/handle.
 * @param {string} [key]
 */
export const clearAuthorCache = (key) => {
    if (key) {
        expertByUidCache.delete(key);
        expertByHandleCache.delete(key);
        fintelCache.delete(key);
        return;
    }
    expertByUidCache.clear();
    expertByHandleCache.clear();
    fintelCache.clear();
};
